import { HashRouter, Routes, Route, Navigate, NavLink } from 'react-router-dom';
import { Hammer, Play, Settings } from 'lucide-react';
import { cn } from '@/utils';
import { BuildPage } from '@/routes/build';
import { PlayPage } from '@/routes/play';

/**
 * Main App component with hash-based routing for GitHub Pages compatibility.
 */
export function App() {
  return (
    <HashRouter>
      <div className="min-h-screen bg-background text-foreground">
        <Routes>
          {/* Default redirect to build */}
          <Route path="/" element={<Navigate to="/build" replace />} />

          {/* Main routes */}
          <Route path="/build" element={<BuildPage />} />
          <Route path="/play" element={<PlayPage />} />
          <Route path="/settings" element={<PlaceholderRoute name="Settings" icon="settings" />} />

          {/* 404 fallback */}
          <Route path="*" element={<Navigate to="/build" replace />} />
        </Routes>

        {/* Bottom Navigation */}
        <BottomNav />
      </div>
    </HashRouter>
  );
}

/**
 * Bottom navigation bar for mobile-first navigation.
 */
function BottomNav() {
  const navItems = [
    { to: '/build', label: 'Build', icon: Hammer },
    { to: '/play', label: 'Play', icon: Play },
    { to: '/settings', label: 'Settings', icon: Settings },
  ];

  return (
    <nav
      className={cn(
        'fixed bottom-0 left-0 right-0 z-50',
        'border-t border-border bg-background/95 backdrop-blur',
        'supports-[backdrop-filter]:bg-background/80',
        'pb-safe'
      )}
      aria-label="Main navigation"
    >
      <div className="container flex h-16 items-center justify-around px-2">
        {navItems.map(({ to, label, icon: Icon }) => (
          <NavLink
            key={to}
            to={to}
            className={({ isActive }) =>
              cn(
                'flex flex-col items-center justify-center gap-1',
                'min-h-[44px] min-w-[44px] rounded-lg px-3 py-2',
                'text-xs font-medium transition-colors',
                'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
                isActive
                  ? 'text-primary'
                  : 'text-muted-foreground hover:text-foreground'
              )
            }
          >
            <Icon className="h-5 w-5" />
            <span>{label}</span>
          </NavLink>
        ))}
      </div>
    </nav>
  );
}

/**
 * Placeholder component for routes not yet implemented.
 */
function PlaceholderRoute({
  name,
  icon,
}: {
  name: string;
  icon: 'play' | 'settings';
}) {
  const IconComponent = {
    play: Play,
    settings: Settings,
  }[icon];

  return (
    <div className="flex min-h-screen flex-col items-center justify-center p-8 pb-24">
      <IconComponent className="mb-4 h-12 w-12 text-muted-foreground" />
      <h1 className="mb-2 text-2xl font-bold">{name}</h1>
      <p className="text-center text-muted-foreground">
        This route is under development.
        <br />
        Check the SPEC.md for details.
      </p>
    </div>
  );
}
